import { appActions } from '@app';
import { error } from '@sonar-software/xuder';
import isEqual from 'lodash/isEqual';
import { QueryParams } from './useQueryParams';
import { useAction } from './useAction';
import { useOnUnmount } from './useOnUnmount';
import { ErrorOrFailStatusCode, getQueryString, HttpResponse, sonarHttpGet } from '@sonar-software/sonar-http';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseApiResourceResult<T> {
	loading: boolean;
	request: () => void;
	reset: () => void;
	resource?: T;
	response?: HttpResponse<void>;
}

export function useApiResource<T>(
	uri: string | null | undefined,
	queryParams?: QueryParams | null,
	manual?: boolean
): UseApiResourceResult<T> {
	const [ loading, setLoading ] = useState(!manual);
	const [ resource, setResource ] = useState<T>();
	const [ response, setResponse ] = useState<HttpResponse<void>>();
	const queryParamsRef = useRef<QueryParams | null>();
	const isMountedRef = useRef(true);
	const requestCanceled = useRef(false);

	const maintenance = useAction(appActions.updateApplicationMaintenance);
	const logError = useAction(error);

	useOnUnmount(() => {
		isMountedRef.current = false;
	});

	const request = useCallback(async() => {
		if (!uri || !isMountedRef.current) {
			return;
		}

		requestCanceled.current = false;
		setLoading(true);

		const cleanedUri = uri.startsWith('/') ? uri.slice(1) : uri;
		const queryString = getQueryString(queryParams);
		const targetUri = `/api/${cleanedUri}${cleanedUri.includes('?') ? '&' : '?'}${queryString}`;

		try {
			const apiResponse = await sonarHttpGet({
				targetUri
			});

			if (apiResponse.status === ErrorOrFailStatusCode.ServiceUnavailable) {
				maintenance({ locked: true });
			}

			if (!isMountedRef.current || requestCanceled.current) {
				return;
			}

			setResponse(apiResponse);

			if (apiResponse.data) {
				setResource(apiResponse.data);
				setLoading(false);
			} else {
				throw new Error(`Failure retrieving resource at: ${targetUri}`);
			}
		} catch (ex) {
			logError(ex.message);
		}
	}, [ logError, maintenance, queryParams, uri ]);

	const reset = useCallback(() => {
		if (loading) {
			requestCanceled.current = true;
		}

		setResponse(undefined);
		setResource(undefined);
		setLoading(false);
	}, [ loading ]);

	useEffect(() => {
		if (manual || !uri || !isMountedRef.current) {
			return;
		}

		if (queryParams && isEqual(queryParams, queryParamsRef.current)) {
			return;
		}

		queryParamsRef.current = queryParams;

		request();
	}, [ logError, queryParams, manual, uri, request ]);

	return {
		loading,
		request,
		reset,
		resource,
		response
	};
}
